/**
 * THE WEEKLY GRID — project rows × day columns.
 *
 * This is the screen the whole company touches every week, so it is optimised for the
 * hands, not the mouse: land in a cell, type, Tab, type, Tab. Arrow keys move between
 * cells. Enter saves.
 *
 * ── The bug this design exists to prevent ────────────────────────────────────
 *
 * Adding a row used to reset a SUBMITTED sheet back to DRAFT. The cause was structural:
 * status was held in local component state alongside the entries, and any local mutation
 * re-derived it.
 *
 * The rule that fixes it, applied here without exception:
 *
 *     STATUS COMES FROM SERVER STATE. LOCAL STATE HOLDS ONLY ENTRY VALUES.
 *
 * `status` is read from `timesheetQuery.data.timesheet.status` and from nowhere else.
 * `addRow` mutates a local array and issues NO request — adding an empty row is not a
 * change to the timesheet, so it cannot change its status. The save request carries no
 * `status` field, and the route has none to accept.
 *
 * ── Save policy ──────────────────────────────────────────────────────────────
 *
 * Auto-save is debounced and fires only when HOURS or BILLABILITY actually changed. Row
 * structure changes (add/remove an empty row, pick a project on a blank row) do not
 * trigger it. Sheets that are APPROVED or LOCKED never auto-save at all.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertTriangle, CalendarRange, CopyPlus, Plus, RefreshCw, Save, Send, Trash2, Undo2 } from 'lucide-react';

import type { ISODate } from '@shared/types';

import {
  useBookableProjects,
  useOpenTimesheet,
  useSaveEntries,
  useTimesheet,
  useTimesheetAction,
  useTimesheetGridQuery,
  useTimesheetPeriods,
  useTimesheetPrefill,
  type EntryInput,
  type GridRow,
  type WeeklyGridView,
} from '@frontend/api/hooks/timesheets';
import { cn } from '@frontend/lib/cn';
import { formatDateShort, formatHours, todayISO } from '@frontend/lib/format';
import { Button } from '@frontend/components/ui/Button';
import { Select } from '@frontend/components/ui/Field';
import { ConfirmDialog, Modal } from '@frontend/components/ui/Modal';
import { Banner, PageHeader } from '@frontend/components/ui/Page';
import { StatusBadge } from '@frontend/components/ui/StatusBadge';
import { EmptyState, ErrorState, LoadingRows } from '@frontend/components/ui/States';
import { useToast } from '@frontend/components/ui/Toast';

/** A row in the editable grid. `localId` distinguishes rows that do not exist server-side yet. */
interface EditableRow {
  localId: string;
  projectId: string | null;
  projectCode: string | null;
  projectName: string | null;
  clientName: string | null;
  taskCode: string | null;
  leaveType: string | null;
  isBillable: boolean;
  /** date → hours, as the STRING the API expects. Never a float. */
  hours: Record<string, string>;
  isNew: boolean;
}

const IMMUTABLE_STATUSES = new Set(['APPROVED', 'LOCKED']);
const SAVE_DEBOUNCE_MS = 900;

let localCounter = 0;
const nextLocalId = (): string => `local_${++localCounter}`;

function toEditable(row: GridRow): EditableRow {
  const hours: Record<string, string> = {};
  for (const cell of row.cells) hours[cell.date] = cell.hours;
  return {
    localId: row.key,
    projectId: row.projectId,
    projectCode: row.projectCode,
    projectName: row.projectName,
    clientName: row.clientName,
    taskCode: row.taskCode,
    leaveType: row.leaveType,
    isBillable: row.isBillable,
    hours,
    isNew: false,
  };
}

/**
 * Only hours and billability count as a "real" change worth saving.
 *
 * A ROW WITH NO HOURS IS NOT PART OF THE SIGNATURE AT ALL. It used to be included as soon
 * as it had a `projectId` — and the add-row dialog will not let you add one without — so
 * every "Add project" click changed the signature, fired the debounced auto-save, and sent
 * a PATCH carrying exactly the entries that were already there. The file states twice, in
 * its header and beside `addRow`, that adding a row issues NO request; that invariant is
 * the one the original DRAFT-reset bug was fixed by, and it was false.
 *
 * The filter is applied AFTER the hours are reduced to their non-zero entries, so clearing
 * a row's last hour still changes the signature (the row drops out of it) and still saves —
 * which is what deletes the entries server-side.
 */
function saveSignature(rows: readonly EditableRow[]): string {
  return JSON.stringify(
    rows
      .filter((row) => row.projectId !== null || row.leaveType !== null)
      .map((row) => ({
        p: row.projectId,
        t: row.taskCode,
        l: row.leaveType,
        b: row.isBillable,
        h: Object.entries(row.hours)
          .filter(([, value]) => value !== '' && Number(value) !== 0)
          .sort(([a], [b]) => a.localeCompare(b)),
      }))
      .filter((row) => row.h.length > 0),
  );
}

function toEntries(rows: readonly EditableRow[], days: readonly ISODate[]): EntryInput[] {
  const entries: EntryInput[] = [];
  for (const row of rows) {
    if (row.projectId === null && row.leaveType === null) continue;
    for (const date of days) {
      const raw = row.hours[date] ?? '';
      if (raw === '' || Number(raw) === 0) continue;
      entries.push({
        date,
        projectId: row.projectId,
        taskCode: row.taskCode,
        hours: raw,
        isBillable: row.isBillable,
        leaveType: row.leaveType,
      });
    }
  }
  return entries;
}

export function TimesheetGridPage(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const requestedDate = params.get('date') ?? todayISO();
  const toast = useToast();

  const periodsQuery = useTimesheetPeriods(undefined, 8);
  const openTimesheet = useOpenTimesheet();
  const [timesheetId, setTimesheetId] = useState<string | null>(null);

  // Resolve the period → its timesheet, creating it on first use.
  useEffect(() => {
    const periods = periodsQuery.data?.periods;
    if (periods === undefined) return;
    const match = periods.find(
      (candidate) => requestedDate >= candidate.period.startDate && requestedDate <= candidate.period.endDate,
    );
    if (match === undefined) return;
    if (match.timesheetId !== null) {
      setTimesheetId(match.timesheetId);
      return;
    }
    openTimesheet.mutate(
      { date: match.period.startDate },
      { onSuccess: (result) => setTimesheetId(result.timesheet.id) },
    );
    // `openTimesheet` is stable enough for this effect; re-running on identity churn
    // would create a second sheet.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodsQuery.data, requestedDate]);

  if (periodsQuery.isLoading) return <LoadingRows rows={8} columns={9} />;
  if (periodsQuery.isError) {
    return (
      <ErrorState
        error={periodsQuery.error}
        onRetry={() => {
          void periodsQuery.refetch();
        }}
      />
    );
  }

  return (
    <div>
      <PageHeader
        title="My timesheet"
        description="Log hours against the projects you are assigned to. Totals update as you type; the sheet saves itself while it is a draft."
        actions={
          <PeriodSwitcher
            periods={periodsQuery.data?.periods ?? []}
            activeDate={requestedDate}
            onSelect={(date) => setParams({ date })}
          />
        }
      />
      {timesheetId === null ? (
        <LoadingRows rows={6} columns={9} />
      ) : (
        <GridBody
          timesheetId={timesheetId}
          onCopied={() => toast.success('Last period copied', 'Rows were copied in. Fill in the hours and submit.')}
        />
      )}
    </div>
  );
}

function PeriodSwitcher({
  periods,
  activeDate,
  onSelect,
}: {
  periods: { period: { startDate: ISODate; endDate: ISODate; label: string }; state: string; isLate: boolean }[];
  activeDate: ISODate;
  onSelect: (date: ISODate) => void;
}): JSX.Element {
  const active =
    periods.find((row) => activeDate >= row.period.startDate && activeDate <= row.period.endDate)?.period
      .startDate ?? '';
  return (
    <Select
      aria-label="Pay period"
      value={active}
      onChange={(event) => onSelect(event.target.value)}
      className="w-[280px]"
      data-testid="period-switcher"
    >
      {periods.map((row) => (
        <option key={row.period.startDate} value={row.period.startDate}>
          {row.period.label}
          {row.state === 'OPEN' ? '' : ` · ${row.state.toLowerCase()}`}
          {row.isLate ? ' · late' : ''}
        </option>
      ))}
    </Select>
  );
}

function GridBody({ timesheetId, onCopied }: { timesheetId: string; onCopied: () => void }): JSX.Element {
  const toast = useToast();
  const gridQuery = useTimesheetGridQuery(timesheetId);
  const detailQuery = useTimesheet(timesheetId);
  const bookable = useBookableProjects();
  const saveEntries = useSaveEntries(timesheetId);
  const { submit, withdraw, copyPrevious } = useTimesheetAction(timesheetId);
  const { fillFromAllocation, recalculate } = useTimesheetPrefill(timesheetId);

  const [rows, setRows] = useState<EditableRow[]>([]);
  const [dirty, setDirty] = useState(false);
  const [confirmSubmit, setConfirmSubmit] = useState(false);
  const [addRowOpen, setAddRowOpen] = useState(false);
  const [warnings, setWarnings] = useState<{ message: string }[]>([]);
  const lastSavedSignature = useRef<string>('');
  const saveTimer = useRef<number | null>(null);

  const grid: WeeklyGridView | undefined = gridQuery.data;
  // ── STATUS IS SERVER STATE. Read here, never derived from `rows`. ──────────
  const status = detailQuery.data?.timesheet.status ?? 'DRAFT';
  const isReadOnly = IMMUTABLE_STATUSES.has(status) || status === 'SUBMITTED';
  const isImmutable = IMMUTABLE_STATUSES.has(status);

  // Re-seed local rows only when the SHEET changes, not on every server refresh — a
  // refetch mid-typing must not blow away what the user is in the middle of entering.
  useEffect(() => {
    if (grid === undefined) return;
    setRows(grid.rows.map(toEditable));
    lastSavedSignature.current = saveSignature(grid.rows.map(toEditable));
    setDirty(false);
  }, [grid?.timesheetId]); // eslint-disable-line react-hooks/exhaustive-deps

  const days = useMemo(() => grid?.days ?? [], [grid]);
  const dayDates = useMemo(() => days.map((day) => day.date), [days]);

  const doSave = useCallback(() => {
    if (grid === undefined || isReadOnly) return;
    const signature = saveSignature(rows);
    if (signature === lastSavedSignature.current) return;
    lastSavedSignature.current = signature;
    saveEntries.mutate(
      { entries: toEntries(rows, dayDates), mode: 'REPLACE' },
      {
        onSuccess: (response) => {
          setDirty(false);
          setWarnings(response.warnings ?? []);
        },
        onError: (error) => {
          setDirty(true);
          toast.fromError(error, 'Your hours were not saved');
        },
      },
    );
  }, [grid, isReadOnly, rows, dayDates, saveEntries, toast]);

  // Debounced auto-save. Fires only on a real hours/billability change.
  useEffect(() => {
    if (grid === undefined || isReadOnly) return undefined;
    const signature = saveSignature(rows);
    if (signature === lastSavedSignature.current) return undefined;
    setDirty(true);
    if (saveTimer.current !== null) window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(doSave, SAVE_DEBOUNCE_MS);
    return () => {
      if (saveTimer.current !== null) window.clearTimeout(saveTimer.current);
    };
  }, [rows, grid, isReadOnly, doSave]);

  // Warn before leaving with unsaved hours.
  useEffect(() => {
    if (!dirty) return undefined;
    const handler = (event: BeforeUnloadEvent): void => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  if (gridQuery.isLoading || detailQuery.isLoading) return <LoadingRows rows={6} columns={9} />;
  if (gridQuery.isError) {
    return (
      <ErrorState
        error={gridQuery.error}
        onRetry={() => {
          void gridQuery.refetch();
        }}
      />
    );
  }
  if (grid === undefined) return <LoadingRows rows={6} columns={9} />;

  /** Local-only. No request, no status change. */
  function addRow(projectId: string, taskCode: string | null): void {
    const project = bookable.data?.projects.find((candidate) => candidate.projectId === projectId);
    setRows((current) => [
      ...current,
      {
        localId: nextLocalId(),
        projectId,
        projectCode: project?.code ?? null,
        projectName: project?.name ?? null,
        clientName: project?.clientName ?? null,
        taskCode,
        leaveType: null,
        isBillable: project?.isBillable ?? true,
        hours: {},
        isNew: true,
      },
    ]);
    setAddRowOpen(false);
  }

  function removeRow(localId: string): void {
    setRows((current) => current.filter((row) => row.localId !== localId));
  }

  function setHours(localId: string, date: string, value: string): void {
    setRows((current) =>
      current.map((row) => (row.localId === localId ? { ...row, hours: { ...row.hours, [date]: value } } : row)),
    );
  }

  const dailyTotal = (date: string): number =>
    rows.reduce((total, row) => total + (Number(row.hours[date] ?? '0') || 0), 0);
  const rowTotal = (row: EditableRow): number =>
    dayDates.reduce((total, date) => total + (Number(row.hours[date] ?? '0') || 0), 0);
  const grandTotal = dayDates.reduce((total, date) => total + dailyTotal(date), 0);

  const threshold = Number(detailQuery.data?.settings.overtimeThresholdHours ?? '40');

  return (
    <div className="space-y-3">
      {/* ── Status + actions ───────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-2">
        <StatusBadge status={status} data-testid="ts-status-badge" />
        <span className="text-body text-content-secondary">{grid.period.label}</span>
        {dirty && (
          <span className="text-body text-content-secondary" data-testid="unsaved-indicator">
            Unsaved changes…
          </span>
        )}
        {saveEntries.isPending && <span className="text-body text-content-secondary">Saving…</span>}

        <div className="ml-auto flex flex-wrap items-center gap-2">
          {!isReadOnly && (
            <>
              <Button
                size="sm"
                leadingIcon={<CopyPlus className="h-3.5 w-3.5" />}
                onClick={() =>
                  copyPrevious.mutate(
                    { includeHours: false },
                    {
                      onSuccess: () => {
                        onCopied();
                        void gridQuery.refetch();
                      },
                      onError: (error) => toast.fromError(error, 'Could not copy the previous period'),
                    },
                  )
                }
                isLoading={copyPrevious.isPending}
                data-testid="copy-last-week"
              >
                Copy last period
              </Button>
              {/*
                Copying last period was wired and this was not, which is the wrong half to
                have shipped. Copy reproduces LAST period's project mix, so somebody who
                rolled onto a new engagement on Monday bills the old client all week unless
                they notice. This fills from what they are actually staffed to now.
              */}
              <Button
                size="sm"
                leadingIcon={<CalendarRange className="h-3.5 w-3.5" />}
                onClick={() =>
                  fillFromAllocation.mutate(
                    { includeWeekends: false },
                    {
                      onSuccess: (response) => {
                        onCopied();
                        void gridQuery.refetch();
                        // A warning here is usually "you are staffed to nothing this week",
                        // which is the whole answer for the person staring at an empty grid.
                        const notes = response.warnings ?? [];
                        if (notes.length > 0) toast.success('Filled from your allocation', notes[0]);
                      },
                      onError: (error) => toast.fromError(error, 'Could not fill from your allocation'),
                    },
                  )
                }
                isLoading={fillFromAllocation.isPending}
                data-testid="fill-from-allocation"
              >
                Fill from allocation
              </Button>
              <Button
                size="sm"
                leadingIcon={<Plus className="h-3.5 w-3.5" />}
                onClick={() => setAddRowOpen(true)}
                data-testid="add-timesheet-row"
              >
                Add project
              </Button>
              <Button
                size="sm"
                variant="secondary"
                leadingIcon={<Save className="h-3.5 w-3.5" />}
                onClick={doSave}
                disabled={!dirty}
                data-testid="save-timesheet"
              >
                Save
              </Button>
              {/*
                Rebuilds the totals from the rows. Totals drift when an entry is changed
                through another path or a rate moves mid-period, and a total that no longer
                matches its rows is worse than none — it is the number that gets approved.
                Never changes status.
              */}
              <Button
                size="sm"
                variant="ghost"
                leadingIcon={<RefreshCw className="h-3.5 w-3.5" />}
                onClick={() =>
                  recalculate.mutate(undefined, {
                    onSuccess: () => {
                      void gridQuery.refetch();
                      void detailQuery.refetch();
                      toast.success('Totals rebuilt from the rows.');
                    },
                    onError: (error) => toast.fromError(error, 'Could not recalculate the totals'),
                  })
                }
                isLoading={recalculate.isPending}
                data-testid="recalculate-timesheet"
              >
                Recalculate
              </Button>
              <Button
                size="sm"
                variant="primary"
                leadingIcon={<Send className="h-3.5 w-3.5" />}
                onClick={() => setConfirmSubmit(true)}
                disabled={grandTotal === 0}
                data-testid="submit-timesheet"
              >
                Submit
              </Button>
            </>
          )}
          {status === 'SUBMITTED' && (
            <Button
              size="sm"
              leadingIcon={<Undo2 className="h-3.5 w-3.5" />}
              onClick={() =>
                withdraw.mutate(undefined, {
                  onError: (error) => toast.fromError(error, 'Could not withdraw the timesheet'),
                })
              }
              isLoading={withdraw.isPending}
              data-testid="withdraw-timesheet"
            >
              Withdraw
            </Button>
          )}
        </div>
      </div>

      {isImmutable && (
        <Banner tone="info" data-testid="immutable-banner">
          {status === 'LOCKED'
            ? 'This period has been locked for payroll and can no longer be edited. If something is wrong, ask your manager to raise a correction.'
            : 'This timesheet has been approved. To change it, ask your approver to reopen it.'}
        </Banner>
      )}
      {status === 'REJECTED' && detailQuery.data?.timesheet.rejectionReason != null && (
        <Banner tone="error" title="Returned by your approver">
          {detailQuery.data.timesheet.rejectionReason} Correct the hours and submit again.
        </Banner>
      )}

      {/* ── Overtime, per FLSA week ────────────────────────────────────── */}
      <OvertimeNotice grid={grid} rows={rows} threshold={threshold} />

      {warnings.length > 0 && (
        <Banner tone="warning" title="Check these before submitting">
          <ul className="list-disc space-y-0.5 pl-4">
            {warnings.map((warning, index) => (
              <li key={index}>{warning.message}</li>
            ))}
          </ul>
        </Banner>
      )}

      {/* ── The grid ───────────────────────────────────────────────────── */}
      {rows.length === 0 ? (
        <div className="surface-card">
          <EmptyState
            title="No hours logged for this period yet"
            description={
              (bookable.data?.projects.length ?? 0) === 0
                ? 'You are not assigned to any project you can book time to. Ask your project manager to add you to a project — until then there is nothing to log against.'
                : 'Add a project row to start logging hours, or copy last period to bring forward the same projects.'
            }
            action={
              (bookable.data?.projects.length ?? 0) > 0 && !isReadOnly ? (
                <Button variant="primary" onClick={() => setAddRowOpen(true)} data-testid="add-first-row">
                  Add project
                </Button>
              ) : undefined
            }
          />
        </div>
      ) : (
        <div className="surface-card overflow-x-auto">
          <table className="data-table" data-testid="timesheet-grid">
            <thead>
              <tr>
                {/* Sticky project column: the grid scrolls horizontally on a tablet. */}
                <th
                  scope="col"
                  className="sticky left-0 z-table-header min-w-[200px] bg-surface-subtle"
                >
                  Project
                </th>
                {days.map((day) => (
                  <th
                    key={day.date}
                    scope="col"
                    className={cn('numeric w-[64px]', day.isWeekend && 'bg-surface-muted')}
                  >
                    <span className="block text-body font-normal normal-case text-content-tertiary">
                      {day.dayOfWeek}
                    </span>
                    {formatDateShort(day.date)}
                  </th>
                ))}
                <th scope="col" className="numeric w-[72px]">
                  Total
                </th>
                {!isReadOnly && <th scope="col" className="w-9" />}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => (
                <tr key={row.localId} data-testid={`ts-row-${row.projectCode ?? row.localId}`}>
                  <th
                    scope="row"
                    className="sticky left-0 z-raised bg-surface px-2 text-left font-normal"
                  >
                    <span className="block truncate text-row text-content">
                      {row.projectCode ?? row.leaveType ?? 'Unassigned'}
                    </span>
                    <span className="block truncate text-body text-content-tertiary">
                      {row.clientName ?? row.projectName ?? ''}
                      {row.isBillable ? '' : ' · non-billable'}
                    </span>
                  </th>
                  {days.map((day, columnIndex) => (
                    <td key={day.date} className={cn('numeric p-0', day.isWeekend && 'bg-surface-muted/50')}>
                      <HoursCell
                        value={row.hours[day.date] ?? ''}
                        disabled={isReadOnly}
                        rowIndex={rowIndex}
                        columnIndex={columnIndex}
                        label={`${row.projectCode ?? 'row'} ${day.date}`}
                        onChange={(value) => setHours(row.localId, day.date, value)}
                        onCommit={doSave}
                      />
                    </td>
                  ))}
                  <td className="numeric font-medium">{rowTotal(row).toFixed(2)}</td>
                  {!isReadOnly && (
                    <td className="px-1">
                      <button
                        type="button"
                        aria-label={`Remove ${row.projectCode ?? 'row'}`}
                        onClick={() => removeRow(row.localId)}
                        className="rounded-sm p-1 text-content-tertiary hover:bg-surface-hover hover:text-error"
                        data-testid={`remove-row-${row.localId}`}
                      >
                        <Trash2 aria-hidden className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-line-strong">
                <th scope="row" className="sticky left-0 z-raised bg-surface px-2 text-left">
                  Daily total
                </th>
                {days.map((day) => {
                  const total = dailyTotal(day.date);
                  return (
                    <td
                      key={day.date}
                      className={cn(
                        'numeric font-semibold',
                        total > 24 && 'text-error',
                        day.isWeekend && 'bg-surface-muted/50',
                      )}
                      data-testid={`daily-total-${day.date}`}
                    >
                      {total === 0 ? '' : total.toFixed(2)}
                    </td>
                  );
                })}
                <td className="numeric text-section font-semibold" data-testid="grid-total">
                  {grandTotal.toFixed(2)}
                </td>
                {!isReadOnly && <td />}
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      <p className="text-body text-content-secondary">
        Keyboard: arrow keys move between cells, Tab moves right, Enter saves. Hours are entered in{' '}
        {detailQuery.data?.settings.minIncrementHours ?? '0.25'}-hour increments.
      </p>

      <AddRowDialog
        open={addRowOpen}
        onClose={() => setAddRowOpen(false)}
        projects={bookable.data?.projects ?? []}
        existing={rows.map((row) => row.projectId).filter((id): id is string => id !== null)}
        onAdd={addRow}
      />

      <ConfirmDialog
        open={confirmSubmit}
        onCancel={() => setConfirmSubmit(false)}
        onConfirm={() => {
          setConfirmSubmit(false);
          // Flush anything pending first — submitting a sheet whose last keystroke has
          // not landed would submit the wrong hours.
          doSave();
          submit.mutate(
            {},
            {
              onSuccess: () =>
                toast.success('Timesheet submitted', 'It is now with your approver. You can withdraw it until they act.'),
              onError: (error) => toast.fromError(error, 'Could not submit the timesheet'),
            },
          );
        }}
        title="Submit this timesheet?"
        confirmLabel="Submit"
        tone="primary"
        isBusy={submit.isPending}
        body={
          <>
            <p>
              {grandTotal.toFixed(2)} hours will be sent for approval for {grid.period.label}.
            </p>
            <p className="mt-2 text-body text-content-secondary">
              You can withdraw it while it is still awaiting a decision.
            </p>
          </>
        }
      />
    </div>
  );
}

/**
 * One editable cell.
 *
 * Arrow keys move by cell. The grid position is encoded in `data-cell` so navigation is a
 * DOM query rather than a ref matrix that has to be kept in sync with row insertion.
 */
function HoursCell({
  value,
  disabled,
  rowIndex,
  columnIndex,
  label,
  onChange,
  onCommit,
}: {
  value: string;
  disabled: boolean;
  rowIndex: number;
  columnIndex: number;
  label: string;
  onChange: (next: string) => void;
  onCommit: () => void;
}): JSX.Element {
  function move(rowDelta: number, columnDelta: number): void {
    const target = document.querySelector<HTMLInputElement>(
      `[data-cell="${rowIndex + rowDelta}:${columnIndex + columnDelta}"]`,
    );
    target?.focus();
    target?.select();
  }

  return (
    <input
      type="text"
      inputMode="decimal"
      aria-label={`Hours for ${label}`}
      data-cell={`${rowIndex}:${columnIndex}`}
      data-testid={`cell-${rowIndex}-${columnIndex}`}
      disabled={disabled}
      value={value}
      onChange={(event) => {
        const next = event.target.value;
        // Permit only digits and one decimal point while typing; the server validates
        // the increment rule and returns a specific message if it is wrong.
        if (next === '' || /^\d{0,2}(\.\d{0,2})?$/.test(next)) onChange(next);
      }}
      onFocus={(event) => event.target.select()}
      onKeyDown={(event) => {
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          move(1, 0);
        } else if (event.key === 'ArrowUp') {
          event.preventDefault();
          move(-1, 0);
        } else if (event.key === 'ArrowRight' && event.currentTarget.selectionStart === value.length) {
          event.preventDefault();
          move(0, 1);
        } else if (event.key === 'ArrowLeft' && event.currentTarget.selectionStart === 0) {
          event.preventDefault();
          move(0, -1);
        } else if (event.key === 'Enter') {
          event.preventDefault();
          onCommit();
          move(1, 0);
        }
      }}
      className={cn(
        'h-row w-full border-0 bg-transparent px-1 text-right text-row tabular text-content',
        'focus:bg-surface-selected disabled:text-content-secondary',
        value !== '' && Number(value) > 0 && 'font-medium',
      )}
      placeholder={disabled ? '' : '·'}
    />
  );
}

function OvertimeNotice({
  grid,
  rows,
  threshold,
}: {
  grid: WeeklyGridView;
  rows: readonly EditableRow[];
  threshold: number;
}): JSX.Element | null {
  const overWeeks = grid.weeks
    .map((week) => {
      const worked = week.days.reduce(
        (total, date) => total + rows.reduce((sum, row) => sum + (Number(row.hours[date] ?? '0') || 0), 0),
        0,
      );
      return { week, worked };
    })
    .filter((entry) => entry.worked > threshold);

  if (overWeeks.length === 0) return null;

  return (
    <Banner tone="warning" title="Overtime in this period" data-testid="overtime-badge">
      <ul className="space-y-0.5">
        {overWeeks.map((entry) => (
          <li key={entry.week.weekStart} className="flex items-center gap-1.5">
            <AlertTriangle aria-hidden className="h-3 w-3 shrink-0" />
            Week of {formatDateShort(entry.week.weekStart)}: {entry.worked.toFixed(2)} hours —{' '}
            {(entry.worked - threshold).toFixed(2)} over the {formatHours(String(threshold))}-hour threshold.
          </li>
        ))}
      </ul>
      <p className="mt-1 text-body text-content-secondary">
        Whether these hours are paid at an overtime rate depends on your FLSA classification. Your approver
        will see the same figures.
      </p>
    </Banner>
  );
}

function AddRowDialog({
  open,
  onClose,
  projects,
  existing,
  onAdd,
}: {
  open: boolean;
  onClose: () => void;
  projects: { projectId: string; code: string; name: string; clientName: string }[];
  existing: readonly string[];
  onAdd: (projectId: string, taskCode: string | null) => void;
}): JSX.Element {
  const [selected, setSelected] = useState('');
  const available = projects.filter((project) => !existing.includes(project.projectId));

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add a project row"
      size="sm"
      data-testid="add-row-dialog"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={selected === ''}
            onClick={() => {
              onAdd(selected, null);
              setSelected('');
            }}
            data-testid="confirm-add-row"
          >
            Add row
          </Button>
        </>
      }
    >
      {available.length === 0 ? (
        <EmptyState
          title="Nothing left to add"
          description={
            projects.length === 0
              ? 'You are not assigned to any project for this period. Ask your project manager to add you to a project.'
              : 'Every project you can book to is already on this timesheet.'
          }
        />
      ) : (
        <label className="block">
          <span className="mb-1 block text-body font-medium text-content-secondary">Project</span>
          <Select
            value={selected}
            onChange={(event) => setSelected(event.target.value)}
            autoFocus
            data-testid="select-project"
          >
            <option value="">Select a project…</option>
            {available.map((project) => (
              <option key={project.projectId} value={project.projectId}>
                {project.code} — {project.name} ({project.clientName})
              </option>
            ))}
          </Select>
        </label>
      )}
    </Modal>
  );
}
